import React from "react";
import { observer } from "mobx-react";
import { Grid, Box } from "@mui/material";
import { useRootStore } from "../../../../store/contexts";
import { GroupContainer } from "../../../../common/components";
import { FormToolbox } from "../../../../common/controls/forms/FormToolbox";
import { FormSaveFooter } from "../../../../common/controls/forms/FormSaveFooter";
import { FormRecordProvider } from "../../../../common/controls/forms/FormRecordContext";
import FormExtras from "../../../../common/components/formExtras";
import { PublisherUpdateStore } from "../../store";
import { runInAction } from "mobx";
import { PersonType } from "../../../../api/graphql";
import { InputReference } from "../../../common/controls";
import { InputString } from "../../../common/controls";
import { MaritalStatus } from "../../../../api/graphql";
import { CreateEntityDialog } from "../../../common/controls";
import { CountryCreate } from "../../../country/components/create/CountryCreate";
import { CountryCreateStore } from "../../../country/store";
import { InputStringMask } from "../../../common/controls";
import { InputPhone } from "../../../common/controls";
import { EmailVerificationIndicator } from "../../../common/controls/EmailVerificationIndicator";
import { FormControl } from "@mui/material";
import { ProvinceStateCreate } from "../../../provinceState/components/create/ProvinceStateCreate";
import { ProvinceStateCreateStore } from "../../../provinceState/store";
import { PublisherCaeIpiCustomField } from "../custom/PublisherCaeIpiCustomField";
import { ItemsCRUDCompact } from "../../../common/controls";
import { CrudRow } from "../../../common/types";
import { GridChangeSet } from "../../../common/types";
import { PublisherRepresentativeUpdate } from "../../../publisherRepresentative/components/update/PublisherRepresentativeUpdate";
import { PublisherRepresentativeUpdateStore } from "../../../publisherRepresentative/store";
import { GridColumn } from "../../../common/types";
import { PublisherCollectingSocietyUpdate } from "../../../publisherCollectingSociety/components/update/PublisherCollectingSocietyUpdate";
import { PublisherCollectingSocietyUpdateStore } from "../../../publisherCollectingSociety/store";
import { ShowDateTime } from "../../../common/controls";
import { PersonaFisicaNameFields } from "../common/PersonaFisicaNameFields";

export type PublisherUpdateFormProps = {
  onSave?: () => void;
  onCancel?: () => void;
  /** Mientras se guarda, Guardar queda deshabilitado. */
  saving?: boolean;
  showToolbox?: boolean;
  showSaveFooter?: boolean;
  recordId?: string;
  personTypeOptions: { id: string; displayId: string }[];
  personTypeError?: string;
  personType: PersonType;
  onPersonTypeChange?: (value: PersonType) => void;
  name: string;
  nameSelectedValue?: { displayId: string; id: string } | null;
  onNameChange?: (value: string) => void;
  nameError?: string;
  maritalStatusOptions: { id: string; displayId: string }[];
  maritalStatusError?: string;
  maritalStatus: MaritalStatus | "";
  onMaritalStatusChange?: (value: MaritalStatus | "") => void;
  nationalitySelectedValue?: { displayId: string; id: string } | null;
  onNationalityChange?: (value: string) => void;
  onNationalitySelected?: (
    value: { displayId: string; id: string } | null
  ) => void;
  nationalityOptions?: { displayId: string; id: string }[];
  nationalityError?: string;
  pseudonym: string;
  pseudonymSelectedValue?: { displayId: string; id: string } | null;
  onPseudonymChange?: (value: string) => void;
  pseudonymError?: string;
  taxId: string;
  taxIdSelectedValue?: { displayId: string; id: string } | null;
  onTaxIdChange?: (value: string) => void;
  taxIdError?: string;
  phone: string;
  phoneSelectedValue?: { displayId: string; id: string } | null;
  onPhoneChange?: (value: string) => void;
  phoneError?: string;
  email: string;
  emailSelectedValue?: { displayId: string; id: string } | null;
  onEmailChange?: (value: string) => void;
  emailError?: string;
  streetAndNumber: string;
  streetAndNumberSelectedValue?: { displayId: string; id: string } | null;
  onStreetAndNumberChange?: (value: string) => void;
  streetAndNumberError?: string;
  floor: string;
  floorSelectedValue?: { displayId: string; id: string } | null;
  onFloorChange?: (value: string) => void;
  floorError?: string;
  suite: string;
  suiteSelectedValue?: { displayId: string; id: string } | null;
  onSuiteChange?: (value: string) => void;
  suiteError?: string;
  postalCode: string;
  postalCodeSelectedValue?: { displayId: string; id: string } | null;
  onPostalCodeChange?: (value: string) => void;
  postalCodeError?: string;
  countrySelectedValue?: { displayId: string; id: string } | null;
  onCountryChange?: (value: string) => void;
  onCountrySelected?: (value: { displayId: string; id: string } | null) => void;
  countryOptions?: { displayId: string; id: string }[];
  countryError?: string;
  provinceSelectedValue?: { displayId: string; id: string } | null;
  onProvinceChange?: (value: string) => void;
  onProvinceSelected?: (
    value: { displayId: string; id: string } | null
  ) => void;
  provinceOptions?: { displayId: string; id: string }[];
  provinceError?: string;
  city: string;
  citySelectedValue?: { displayId: string; id: string } | null;
  onCityChange?: (value: string) => void;
  cityError?: string;
  caeIpi: string;
  caeIpiSelectedValue?: { displayId: string; id: string } | null;
  onCaeIpiChange?: (value: string) => void;
  caeIpiError?: string;
  representativesOnRestore: (id: string) => void;
  representativesOnCommitChanges: (changes: GridChangeSet) => void;
  representativesOnCommitInlineChange: (
    rowId: string,
    changes: Record<string, any>
  ) => void;
  representativesValidateChanges: (changes: GridChangeSet) => boolean;
  representativesRows: CrudRow[];
  representativesError?: string;
  publisherRepresentativeUpdateStore: PublisherRepresentativeUpdateStore;
  representativesVisibleColumns: GridColumn[];
  representativesColumnTypes: Record<
    string,
    {
      fieldType:
        | "String"
        | "Int"
        | "Decimal"
        | "Boolean"
        | "Date"
        | "DateTime"
        | "__Reference"
        | "File"
        | "__Enum";
      nullable?: boolean;
      readOnly?: boolean;
    }
  >;
  managementSocietiesOnRestore: (id: string) => void;
  managementSocietiesOnCommitChanges: (changes: GridChangeSet) => void;
  managementSocietiesOnCommitInlineChange: (
    rowId: string,
    changes: Record<string, any>
  ) => void;
  managementSocietiesValidateChanges: (changes: GridChangeSet) => boolean;
  managementSocietiesRows: CrudRow[];
  managementSocietiesError?: string;
  publisherCollectingSocietyUpdateStore: PublisherCollectingSocietyUpdateStore;
  managementSocietiesVisibleColumns: GridColumn[];
  managementSocietiesColumnTypes: Record<
    string,
    {
      fieldType:
        | "String"
        | "Int"
        | "Decimal"
        | "Boolean"
        | "Date"
        | "DateTime"
        | "__Reference"
        | "File"
        | "__Enum";
      nullable?: boolean;
      readOnly?: boolean;
    }
  >;
  createdAt_: Date | null;
  lastUpdatedAt_: Date | null;
  dummyCRUDRestoredObservable: number;
  publisherUpdateStore: PublisherUpdateStore;
};

export const PublisherUpdateForm: React.FC<PublisherUpdateFormProps> = observer(
  ({
    onSave = () => {},
    onCancel = () => {},
    saving = false,
    showToolbox = true,
    showSaveFooter,
    recordId,
    personTypeOptions = [],
    personTypeError,
    personType = "" as PersonType,
    onPersonTypeChange = () => {},
    name = "",
    nameError,
    onNameChange = () => {},
    maritalStatusOptions = [],
    maritalStatusError,
    maritalStatus = "" as MaritalStatus | "",
    onMaritalStatusChange = () => {},
    onNationalitySelected = () => {},
    nationalityOptions = [],
    nationalitySelectedValue = null,
    nationalityError,
    onNationalityChange = () => {},
    pseudonym = "",
    pseudonymError,
    onPseudonymChange = () => {},
    taxId = "",
    taxIdError,
    onTaxIdChange = () => {},
    phone = "",
    phoneError,
    onPhoneChange = () => {},
    email = "",
    emailError,
    onEmailChange = () => {},
    streetAndNumber = "",
    streetAndNumberError,
    onStreetAndNumberChange = () => {},
    floor = "",
    floorError,
    onFloorChange = () => {},
    suite = "",
    suiteError,
    onSuiteChange = () => {},
    postalCode = "",
    postalCodeError,
    onPostalCodeChange = () => {},
    onCountrySelected = () => {},
    countryOptions = [],
    countrySelectedValue = null,
    countryError,
    onCountryChange = () => {},
    onProvinceSelected = () => {},
    provinceOptions = [],
    provinceSelectedValue = null,
    provinceError,
    onProvinceChange = () => {},
    city = "",
    cityError,
    onCityChange = () => {},
    caeIpi = "",
    caeIpiError,
    onCaeIpiChange = () => {},
    representativesOnRestore,
    representativesOnCommitChanges,
    representativesOnCommitInlineChange,
    representativesValidateChanges,
    representativesRows,
    representativesError,
    publisherRepresentativeUpdateStore,
    representativesVisibleColumns,
    representativesColumnTypes,
    managementSocietiesOnRestore,
    managementSocietiesOnCommitChanges,
    managementSocietiesOnCommitInlineChange,
    managementSocietiesValidateChanges,
    managementSocietiesRows,
    managementSocietiesError,
    publisherCollectingSocietyUpdateStore,
    managementSocietiesVisibleColumns,
    managementSocietiesColumnTypes,
    createdAt_ = null,
    lastUpdatedAt_ = null,
    dummyCRUDRestoredObservable,
    publisherUpdateStore,
  }) => {
    const rootStore = useRootStore();
    const { langStore } = rootStore;

    if (publisherRepresentativeUpdateStore.status === "uninitialized") {
      publisherRepresentativeUpdateStore.initialize();
    }

    if (publisherCollectingSocietyUpdateStore.status === "uninitialized") {
      publisherCollectingSocietyUpdateStore.initialize();
    }

    return (
      <Grid>
        {showToolbox && (
          <FormToolbox
            saveAndCloseLabel={langStore.saveEntity}
            closeLabel={langStore.cancelUpdatedEntity}
            saveForm={() => onSave()}
            saveDisabled={saving}
            closeForm={() => onCancel()}
          />
        )}
        <Box>
          <FormRecordProvider
            recordId={recordId}
            values={{
              personType,
              name,
              maritalStatus,
              nationality: nationalitySelectedValue,
              pseudonym,
              taxId,
              phone,
              email,
              streetAndNumber,
              floor,
              suite,
              postalCode,
              country: countrySelectedValue,
              province: provinceSelectedValue,
              city,
              caeIpi,
              createdAt_,
              lastUpdatedAt_,
            }}
          >
            <FormExtras
              entity="Publisher"
              mode="update"
              recordId={recordId}
              values={{
                personType,
                name,
                maritalStatus,
                nationality: nationalitySelectedValue,
                pseudonym,
                taxId,
                phone,
                email,
                streetAndNumber,
                floor,
                suite,
                postalCode,
                country: countrySelectedValue,
                province: provinceSelectedValue,
                city,
                caeIpi,
                createdAt_,
                lastUpdatedAt_,
              }}
              options={{
                nationality: nationalityOptions,
                country: countryOptions,
                province: provinceOptions,
              }}
              onPatch={(campo: string, valor: any) => {
                switch (campo) {
                  case "personType":
                    onPersonTypeChange?.(valor);
                    break;
                  case "name":
                    onNameChange?.(valor);
                    break;
                  case "maritalStatus":
                    onMaritalStatusChange?.(valor);
                    break;
                  case "nationality":
                    onNationalitySelected?.(valor);
                    break;
                  case "pseudonym":
                    onPseudonymChange?.(valor);
                    break;
                  case "taxId":
                    onTaxIdChange?.(valor);
                    break;
                  case "phone":
                    onPhoneChange?.(valor);
                    break;
                  case "email":
                    onEmailChange?.(valor);
                    break;
                  case "streetAndNumber":
                    onStreetAndNumberChange?.(valor);
                    break;
                  case "floor":
                    onFloorChange?.(valor);
                    break;
                  case "suite":
                    onSuiteChange?.(valor);
                    break;
                  case "postalCode":
                    onPostalCodeChange?.(valor);
                    break;
                  case "country":
                    onCountrySelected?.(valor);
                    break;
                  case "province":
                    onProvinceSelected?.(valor);
                    break;
                  case "city":
                    onCityChange?.(valor);
                    break;
                  case "caeIpi":
                    onCaeIpiChange?.(valor);
                    break;
                }
              }}
            />

            <GroupContainer
              id="basicInformation"
              errorText={publisherUpdateStore.getCardErrors("basicInformation")}
              title={langStore.groupPublisherBasicInformationLabel}
              collapsible={true}
              expanded={publisherUpdateStore.getCardState("basicInformation")}
              toggleExpand={() =>
                publisherUpdateStore.toggleCardState("basicInformation")
              }
            >
              <FormControl fullWidth>
                <InputReference
                  id="personType"
                  autoFocus
                  disabled={false}
                  error={Boolean(personTypeError)}
                  helperText={
                    personTypeError ||
                    publisherUpdateStore.warnings["personType"]?.message
                  }
                  label={langStore.PublisherPersonTypeLabel}
                  options={personTypeOptions}
                  selectedValue={
                    personType
                      ? {
                          id: personType,
                          displayId:
                            personTypeOptions.find(
                              (o: { id: string }) => o.id === personType
                            )?.displayId || personType,
                        }
                      : null
                  }
                  onChange={() => {}}
                  onSelect={(value: { id: string; displayId: string } | null) =>
                    onPersonTypeChange(
                      (value?.id as PersonType) || ("" as PersonType)
                    )
                  }
                />
                {personType === "naturalPerson" ? (
                  <>
                    <PersonaFisicaNameFields
                      value={name}
                      onChange={(value: string) => onNameChange(value)}
                      error={Boolean(nameError)}
                      helperText={nameError}
                    />
                    <InputReference
                      id="maritalStatus"
                      disabled={false}
                      error={Boolean(maritalStatusError)}
                      helperText={
                        maritalStatusError ||
                        publisherUpdateStore.warnings["maritalStatus"]?.message
                      }
                      label={langStore.PublisherMaritalStatusLabel}
                      options={maritalStatusOptions}
                      nullText={langStore.optionalFieldHint}
                      selectedValue={
                        maritalStatus
                          ? {
                              id: maritalStatus,
                              displayId:
                                maritalStatusOptions.find(
                                  (o: { id: string }) => o.id === maritalStatus
                                )?.displayId || maritalStatus,
                            }
                          : null
                      }
                      onChange={() => {}}
                      onSelect={(value: { id: string; displayId: string } | null) =>
                        onMaritalStatusChange(
                          (value?.id as MaritalStatus | "") ||
                            ("" as MaritalStatus | "")
                        )
                      }
                    />
                    <InputReference
                      id="nationality"
                      label={langStore.PublisherNationalityLabel}
                      error={Boolean(nationalityError)}
                      helperText={
                        nationalityError ||
                        publisherUpdateStore.warnings["nationality"]?.message
                      }
                      selectedValue={nationalitySelectedValue}
                      onChange={(value: string) => onNationalityChange(value)}
                      onSelect={(value: { displayId: string; id: string } | null) =>
                        onNationalitySelected(value)
                      }
                      options={nationalityOptions}
                      nullText={langStore.optionalFieldHint}
                      description={langStore.PublisherNationalityDescription}
                      canCreate={rootStore.authStore.hasPermissions(
                        "country_create"
                      )}
                      renderCreateDialog={(dialogProps) => (
                        <CreateEntityDialog
                          {...dialogProps}
                          createStore={(rootStore) =>
                            new CountryCreateStore(rootStore)
                          }
                          renderForm={(store) => (
                            <CountryCreate
                              countryCreateStore={store}
                              showToolbox={false}
                              showSaveFooter={false}
                            />
                          )}
                          saveMethod="saveNewCountry"
                          cancelMethod="cancelNewCountry"
                        />
                      )}
                    />
                  </>
                ) : (
                  <InputString
                    value={name}
                    error={Boolean(nameError)}
                    warning={
                      !nameError && Boolean(publisherUpdateStore.warnings["name"])
                    }
                    helperText={
                      nameError || publisherUpdateStore.warnings["name"]?.message
                    }
                    label={langStore.PublisherNameLabel}
                    onChange={(value: string) => onNameChange(value)}
                    description={langStore.PublisherNameDescription}
                  />
                )}
                <InputString
                  value={pseudonym}
                  error={Boolean(pseudonymError)}
                  warning={
                    !pseudonymError &&
                    Boolean(publisherUpdateStore.warnings["pseudonym"])
                  }
                  helperText={
                    pseudonymError ||
                    publisherUpdateStore.warnings["pseudonym"]?.message
                  }
                  label={langStore.PublisherPseudonymLabel}
                  onChange={(value: string) => onPseudonymChange(value)}
                  nullText={langStore.optionalFieldHint}
                  description={langStore.PublisherPseudonymDescription}
                />
                <InputStringMask
                  value={taxId}
                  error={Boolean(taxIdError)}
                  warning={
                    !taxIdError &&
                    Boolean(publisherUpdateStore.warnings["taxId"])
                  }
                  helperText={
                    taxIdError ||
                    publisherUpdateStore.warnings["taxId"]?.message
                  }
                  label={langStore.PublisherTaxIdLabel}
                  onChange={(value: string) => onTaxIdChange(value)}
                  maskConfig={{ mask: "99-99999999-9" }}
                  description={langStore.PublisherTaxIdDescription}
                />
                <InputPhone
                  value={phone}
                  error={Boolean(phoneError)}
                  helperText={
                    phoneError ||
                    publisherUpdateStore.warnings["phone"]?.message
                  }
                  label={langStore.PublisherPhoneLabel}
                  onChange={(value: string) => onPhoneChange(value)}
                  description={langStore.PublisherPhoneDescription}
                />
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    flexWrap: "wrap",
                  }}
                >
                  <InputString
                    value={email}
                    error={Boolean(emailError)}
                    warning={
                      !emailError &&
                      Boolean(publisherUpdateStore.warnings["email"])
                    }
                    helperText={
                      emailError ||
                      publisherUpdateStore.warnings["email"]?.message
                    }
                    label={langStore.PublisherEmailLabel}
                    onChange={(value: string) => onEmailChange(value)}
                    description={langStore.PublisherEmailDescription}
                  />
                  <EmailVerificationIndicator
                    email={email}
                    entityName="Publisher"
                  />
                </Box>
              </FormControl>
            </GroupContainer>

            <GroupContainer
              id="address"
              errorText={publisherUpdateStore.getCardErrors("address")}
              title={langStore.groupPublisherAddressLabel}
              collapsible={true}
              expanded={publisherUpdateStore.getCardState("address")}
              toggleExpand={() =>
                publisherUpdateStore.toggleCardState("address")
              }
            >
              <FormControl fullWidth>
                <InputString
                  value={streetAndNumber}
                  error={Boolean(streetAndNumberError)}
                  warning={
                    !streetAndNumberError &&
                    Boolean(publisherUpdateStore.warnings["streetAndNumber"])
                  }
                  helperText={
                    streetAndNumberError ||
                    publisherUpdateStore.warnings["streetAndNumber"]?.message
                  }
                  label={langStore.PublisherStreetAndNumberLabel}
                  onChange={(value: string) => onStreetAndNumberChange(value)}
                  description={langStore.PublisherStreetAndNumberDescription}
                />
                <InputString
                  value={floor}
                  error={Boolean(floorError)}
                  warning={
                    !floorError &&
                    Boolean(publisherUpdateStore.warnings["floor"])
                  }
                  helperText={
                    floorError ||
                    publisherUpdateStore.warnings["floor"]?.message
                  }
                  label={langStore.PublisherFloorLabel}
                  onChange={(value: string) => onFloorChange(value)}
                  nullText={langStore.optionalFieldHint}
                  description={langStore.PublisherFloorDescription}
                />
                <InputString
                  value={suite}
                  error={Boolean(suiteError)}
                  warning={
                    !suiteError &&
                    Boolean(publisherUpdateStore.warnings["suite"])
                  }
                  helperText={
                    suiteError ||
                    publisherUpdateStore.warnings["suite"]?.message
                  }
                  label={langStore.PublisherSuiteLabel}
                  onChange={(value: string) => onSuiteChange(value)}
                  nullText={langStore.optionalFieldHint}
                  description={langStore.PublisherSuiteDescription}
                />
                <InputString
                  value={postalCode}
                  error={Boolean(postalCodeError)}
                  warning={
                    !postalCodeError &&
                    Boolean(publisherUpdateStore.warnings["postalCode"])
                  }
                  helperText={
                    postalCodeError ||
                    publisherUpdateStore.warnings["postalCode"]?.message
                  }
                  label={langStore.PublisherPostalCodeLabel}
                  onChange={(value: string) => onPostalCodeChange(value)}
                  description={langStore.PublisherPostalCodeDescription}
                />
                <InputReference
                  id="country"
                  label={langStore.PublisherCountryLabel}
                  error={Boolean(countryError)}
                  helperText={
                    countryError ||
                    publisherUpdateStore.warnings["country"]?.message
                  }
                  selectedValue={countrySelectedValue}
                  onChange={(value: string) => onCountryChange(value)}
                  onSelect={(value: { displayId: string; id: string } | null) =>
                    onCountrySelected(value)
                  }
                  options={countryOptions}
                  description={langStore.PublisherCountryDescription}
                  canCreate={rootStore.authStore.hasPermissions(
                    "country_create"
                  )}
                  renderCreateDialog={(dialogProps) => (
                    <CreateEntityDialog
                      {...dialogProps}
                      createStore={(rootStore) =>
                        new CountryCreateStore(rootStore)
                      }
                      renderForm={(store) => (
                        <CountryCreate
                          countryCreateStore={store}
                          showToolbox={false}
                          showSaveFooter={false}
                        />
                      )}
                      saveMethod="saveNewCountry"
                      cancelMethod="cancelNewCountry"
                    />
                  )}
                />
                <InputReference
                  id="province"
                  label={langStore.PublisherProvinceLabel}
                  error={Boolean(provinceError)}
                  helperText={
                    provinceError ||
                    publisherUpdateStore.warnings["province"]?.message
                  }
                  selectedValue={provinceSelectedValue}
                  onChange={(value: string) => onProvinceChange(value)}
                  onSelect={(value: { displayId: string; id: string } | null) =>
                    onProvinceSelected(value)
                  }
                  options={provinceOptions}
                  description={langStore.PublisherProvinceDescription}
                  canCreate={rootStore.authStore.hasPermissions(
                    "provinceState_create"
                  )}
                  renderCreateDialog={(dialogProps) => (
                    <CreateEntityDialog
                      {...dialogProps}
                      createStore={(rootStore) =>
                        new ProvinceStateCreateStore(rootStore)
                      }
                      renderForm={(store) => (
                        <ProvinceStateCreate
                          provinceStateCreateStore={store}
                          showToolbox={false}
                          showSaveFooter={false}
                        />
                      )}
                      saveMethod="saveNewProvinceState"
                      cancelMethod="cancelNewProvinceState"
                    />
                  )}
                />
                <InputString
                  value={city}
                  error={Boolean(cityError)}
                  warning={
                    !cityError && Boolean(publisherUpdateStore.warnings["city"])
                  }
                  helperText={
                    cityError || publisherUpdateStore.warnings["city"]?.message
                  }
                  label={langStore.PublisherCityLabel}
                  onChange={(value: string) => onCityChange(value)}
                  description={langStore.PublisherCityDescription}
                />
              </FormControl>
            </GroupContainer>

            <GroupContainer
              id="legalInformation"
              errorText={publisherUpdateStore.getCardErrors("legalInformation")}
              title={langStore.groupPublisherLegalInformationLabel}
              collapsible={true}
              expanded={publisherUpdateStore.getCardState("legalInformation")}
              toggleExpand={() =>
                publisherUpdateStore.toggleCardState("legalInformation")
              }
            >
              <FormControl fullWidth>
                <PublisherCaeIpiCustomField
                  value={caeIpi}
                  error={Boolean(caeIpiError)}
                  helperText={
                    caeIpiError ||
                    publisherUpdateStore.warnings["caeIpi"]?.message
                  }
                  {...{ optional: false }}
                  label={langStore.PublisherCaeIpiLabel}
                  description={langStore.PublisherCaeIpiDescription}
                  onChange={(value: string) => onCaeIpiChange(value)}
                />
                <ItemsCRUDCompact
                  label={langStore.PublisherRepresentativesLabel}
                  description={langStore.PublisherRepresentativesDescription}
                  showLabel={true}
                  rows={representativesRows}
                  columns={representativesVisibleColumns}
                  columnTypes={representativesColumnTypes}
                  canCreate={true}
                  isEditVisible={() =>
                    rootStore.authStore.hasPermissions(
                      "publisherRepresentative_update"
                    )
                  }
                  isDeleteVisible={() =>
                    rootStore.authStore.hasPermissions(
                      "publisherRepresentative_delete"
                    )
                  }
                  popupComponent={(row: any) => {
                    if (
                      row &&
                      row.id &&
                      !(
                        typeof row.id === "string" && row.id.startsWith("auto-")
                      )
                    ) {
                      runInAction(() =>
                        publisherUpdateStore.representativesLoadRow(row)
                      );
                    }
                    return (
                      <PublisherRepresentativeUpdate
                        publisherRepresentativeUpdateStore={
                          publisherRepresentativeUpdateStore
                        }
                        showToolbox={false}
                        showSaveFooter={false}
                      />
                    );
                  }}
                  popupMaxWidth="md"
                  onRestore={(id: string) => representativesOnRestore(id)}
                  onCommitChanges={(changes: GridChangeSet) =>
                    representativesOnCommitChanges(changes)
                  }
                  onCommitInlineChange={(
                    rowId: string,
                    changes: Record<string, any>
                  ) => representativesOnCommitInlineChange(rowId, changes)}
                  validate={(changes: GridChangeSet) =>
                    representativesValidateChanges(changes)
                  }
                  dummyCRUDRestoredObservable={dummyCRUDRestoredObservable}
                  error={representativesError}
                />
                <ItemsCRUDCompact
                  label={langStore.PublisherManagementSocietiesLabel}
                  description={
                    langStore.PublisherManagementSocietiesDescription
                  }
                  showLabel={true}
                  rows={managementSocietiesRows}
                  columns={managementSocietiesVisibleColumns}
                  columnTypes={managementSocietiesColumnTypes}
                  canCreate={true}
                  isEditVisible={() =>
                    rootStore.authStore.hasPermissions(
                      "publisherCollectingSociety_update"
                    )
                  }
                  isDeleteVisible={() =>
                    rootStore.authStore.hasPermissions(
                      "publisherCollectingSociety_delete"
                    )
                  }
                  popupComponent={(row: any) => {
                    if (
                      row &&
                      row.id &&
                      !(
                        typeof row.id === "string" && row.id.startsWith("auto-")
                      )
                    ) {
                      runInAction(() =>
                        publisherUpdateStore.managementSocietiesLoadRow(row)
                      );
                    }
                    return (
                      <PublisherCollectingSocietyUpdate
                        publisherCollectingSocietyUpdateStore={
                          publisherCollectingSocietyUpdateStore
                        }
                        showToolbox={false}
                        showSaveFooter={false}
                      />
                    );
                  }}
                  popupMaxWidth="sm"
                  onRestore={(id: string) => managementSocietiesOnRestore(id)}
                  onCommitChanges={(changes: GridChangeSet) =>
                    managementSocietiesOnCommitChanges(changes)
                  }
                  onCommitInlineChange={(
                    rowId: string,
                    changes: Record<string, any>
                  ) => managementSocietiesOnCommitInlineChange(rowId, changes)}
                  validate={(changes: GridChangeSet) =>
                    managementSocietiesValidateChanges(changes)
                  }
                  dummyCRUDRestoredObservable={dummyCRUDRestoredObservable}
                  error={managementSocietiesError}
                />
              </FormControl>
            </GroupContainer>

            <GroupContainer
              id="systemDates"
              errorText={publisherUpdateStore.getCardErrors("systemDates")}
              title={langStore.groupPublisherSystemDatesLabel}
              collapsible={true}
              expanded={publisherUpdateStore.getCardState("systemDates")}
              toggleExpand={() =>
                publisherUpdateStore.toggleCardState("systemDates")
              }
            >
              <FormControl fullWidth>
                <ShowDateTime
                  label={langStore.PublisherCreatedAt_Label}
                  description={langStore.PublisherCreatedAt_Description}
                  value={createdAt_}
                />
                <ShowDateTime
                  label={langStore.PublisherLastUpdatedAt_Label}
                  description={langStore.PublisherLastUpdatedAt_Description}
                  value={lastUpdatedAt_}
                />
              </FormControl>
            </GroupContainer>
          </FormRecordProvider>
        </Box>
        {(showSaveFooter ?? !showToolbox) && (
          <FormSaveFooter
            onSave={() => onSave()}
            label={langStore.saveEntity}
            disabled={saving}
          />
        )}
      </Grid>
    );
  }
);
